// src/components/magdrops/MagDropsWelcome.tsx
import { Footer, Header } from "@/components/shared";
import {
  Calculator,
  CheckCircle,
  ChevronRight,
  Clock,
  Edit3,
  Eye,
  FileText,
  Play,
  Star,
  Target,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";

interface MagDropsWelcomeProps {
  onGetQuote: () => void;
}

export default function MagDropsWelcome({ onGetQuote }: MagDropsWelcomeProps) {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      quote:
        "The head of the local council reached out to work with us having read about us in the magazine.",
      author: "Daisy",
      company: "@honeyhouse.club",
      impact: "Council Partnership",
    },
    {
      quote:
        "Within two days of the magazines going out I got a lead from my article I wrote",
      author: "Katie",
      company: "@katie_hendra_property",
      impact: "Immediate ROI",
    },
    {
      quote:
        "Consumers are 70% more likely to remember a brand they encounter in print versus digital.",
      author: "FIPP Report",
      company: "The Power of Print",
      impact: "Proven Results",
    },
  ];

  const whyChooseUs = [
    {
      title: "Copywriting & Communications Specialists",
      description:
        "We understand how to cut through the noise, engage with your audiences and create something sustainable in this content marathon.",
      icon: Edit3,
    },
    {
      title: "Editorial Enthusiasts",
      description:
        "True word smiths who bring storytelling to life. Sometimes there's nothing better than words on paper in your hand in an age of screens.",
      icon: FileText,
    },
    {
      title: "We Are Entrepreneurs",
      description:
        "We know time is money. Clear, transparent and straight to the point. No thrills - it's about execution and getting it out there.",
      icon: Zap,
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Video Background */}
      <div className="relative min-h-screen">
        {/* Video Background */}
        <div className="absolute inset-0 overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover grayscale"
          >
            <source
              src="/src/assets/videos/magdrops/hero-demo.mp4"
              type="video/mp4"
            />
            {/* Fallback for browsers that don't support video */}
            <div className="w-full h-full bg-slate-50 flex items-center justify-center">
              <div className="text-slate-300 opacity-40">
                <Play className="w-32 h-32" />
              </div>
            </div>
          </video>
          <div className="absolute inset-0 bg-black/30"></div>
          <div className="absolute inset-0 bg-white/60"></div>
        </div>

        {/* Header */}
        <Header />

        {/* Hero Content */}
        <div className="relative z-10 flex items-center justify-center min-h-screen px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center bg-slate-100 rounded-full px-4 py-2 mb-8">
              <FileText className="w-4 h-4 mr-2 text-slate-600" />
              <span className="text-sm font-medium text-slate-700">
                MagDrops
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-slate-900">
              Talk directly to your
              <br />
              <span className="text-blue-600">customers IRL</span>
            </h1>

            <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-12 leading-relaxed">
              A magazine curated and delivered to be dropped into your
              customers' bag within a matter of weeks. Cut through the digital
              noise.
            </p>

            <button
              onClick={onGetQuote}
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg shadow-blue-600/25"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Build your magazine quote
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        </div>
      </div>

      {/* Testimonial Section */}
      <div className="bg-slate-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Trusted by businesses nationwide
            </h2>
            <p className="text-lg text-slate-600">
              Real magazines for real businesses, delivering real results
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-100 max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-slate-700">
                  Client Impact
                </span>
              </div>
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                {testimonials[currentTestimonial].impact}
              </span>
            </div>
            <blockquote className="text-lg text-slate-700 mb-6 italic">
              "{testimonials[currentTestimonial].quote}"
            </blockquote>
            <div className="flex justify-between items-center">
              <div>
                <div className="font-semibold text-slate-900">
                  {testimonials[currentTestimonial].author}
                </div>
                <div className="text-sm text-blue-600">
                  {testimonials[currentTestimonial].company}
                </div>
              </div>
              <div className="flex space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-yellow-400 text-yellow-400"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Why MagDrops works
            </h2>
            <p className="text-lg text-slate-600">No faff, just results</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {whyChooseUs.map((reason, index) => {
              const IconComp = reason.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <IconComp className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-slate-900">
                    {reason.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div className="font-bold text-slate-900 mb-1">4-6 weeks</div>
              <div className="text-sm text-slate-600">From brief to bag</div>
            </div>

            <div className="text-center">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
              <div className="font-bold text-slate-900 mb-1">70% better</div>
              <div className="text-sm text-slate-600">
                Brand recall vs digital
              </div>
            </div>

            <div className="text-center">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div className="font-bold text-slate-900 mb-1">Direct reach</div>
              <div className="text-sm text-slate-600">Physical presence</div>
            </div>

            <div className="text-center">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div className="font-bold text-slate-900 mb-1">Transparent</div>
              <div className="text-sm text-slate-600">No hidden costs</div>
            </div>
          </div>
        </div>
      </div>

      {/* Magazine Examples */}
      <div className="bg-slate-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              See our work
            </h2>
            <p className="text-lg text-slate-600">
              Real magazines, real results
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Magazine Example 1 */}
            <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
              <div className="h-64 bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-blue-600 mx-auto mb-2" />
                  <div className="text-sm text-slate-600">
                    Honey House Community
                  </div>
                  <div className="text-xs text-slate-500">
                    12 pages • Local Business
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-slate-900 mb-2">
                  Community Magazine
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Connecting local businesses with residents through lifestyle
                  content and community stories.
                </p>
                <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full inline-block">
                  Council partnership secured
                </div>
              </div>
            </div>

            {/* Magazine Example 2 */}
            <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
              <div className="h-64 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-slate-600 mx-auto mb-2" />
                  <div className="text-sm text-slate-600">
                    Property Investment
                  </div>
                  <div className="text-xs text-slate-500">
                    24 pages • Professional
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-slate-900 mb-2">
                  Industry Magazine
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Positioning property professional as market expert with
                  investment insights and analysis.
                </p>
                <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full inline-block">
                  Leads within 48 hours
                </div>
              </div>
            </div>

            {/* Magazine Example 3 */}
            <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
              <div className="h-64 bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-amber-600 mx-auto mb-2" />
                  <div className="text-sm text-slate-600">Food Culture</div>
                  <div className="text-xs text-slate-500">
                    16 pages • Retail
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-slate-900 mb-2">
                  Market Traders
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Celebrating local producers and traditional craftsmanship with
                  food culture stories.
                </p>
                <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full inline-block">
                  40% footfall increase
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Width Intro Video Section */}
      <div className="bg-slate-900 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              See The Power Of Print
            </h2>
            <p className="text-lg text-slate-300">
              Watch how we transform your business story into print
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <video
              controls
              className="w-full h-auto"
              poster="" // You can add a poster image here if needed
            >
              <source
                src="/src/assets/videos/magdrops/Magdrops-Intro.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
